import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { DecisionTreeReportQuery } from './useQueryAssistantMutation';
import useStreamText from './useStreamText';

export default function useDecisionTreeReport(queries: (DecisionTreeReportQuery)[]) {
  const [results, setResults] = React.useState<(DecisionTreeReportQuery)[]>(queries);
  const [currentId, setCurrentId] = React.useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleQueryEnd = React.useCallback((id: string, finalText: string) => {
    queryClient.setQueryData(['getDecisionTreeRunResult', { input: { id } }], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        getDecisionTreeRun: {
          ...oldData.getDecisionTreeRun,
          summaryFinishedAt: Date.now(),
          summary: finalText,
        },
      };
    });
    setResults((prev) => {
      const updated = prev.map((item) => (item.id === id ? { ...item, summary: finalText, summaryFinishedAt: Date.now() } : item));
      const next = updated.find((item) => !item.summaryFinishedAt);
      setCurrentId(next?.id || null);
      return updated;
    });
  }, []);

  const { mutate, text: streamingText, loading } = useStreamText(handleQueryEnd);
  React.useEffect(() => {
    if (!streamingText || !currentId) return;
    setResults((prev) => prev.map((item) => (item.id === currentId ? { ...item, summary: streamingText, summaryFinishedAt: Date.now() } : item)));
  }, [streamingText, currentId]);

  React.useEffect(() => {
    if (!currentId) return;
    const current = results.find((r) => r.id === currentId);
    if (current && !current.summary) {
      mutate({ treeRun: current, id: currentId });
    }
  }, [currentId, results]);

  const generateReport = React.useCallback(() => {
    if (!queries?.length) return;
    setResults(queries);
    const firstToGenerate = queries.find((item) => !item.summaryFinishedAt);
    setCurrentId(firstToGenerate?.id || null);
  }, [queries]);

  return {
    results,
    generateReport,
    loading,
  };
}
